import { memo, useEffect, useRef } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import { useAudioObjectUpdateMutation } from 'hooks/mutations/content/AudioObject'
import { audioObjectRawRoute } from 'pages/NWPlatform/Workspaces/Workspace/Audio/Objects/Object'
import { audioObjectQueryKeys } from 'services/queryKeys'

const JSONRaw = () => {
  const { t } = useTranslation(['domain'])
  const { enqueueSnackbar } = useSnackbar()

  const editorInstanceRef = useRef<EditorInstance>(undefined)

  const { audioobjectId: objectId } = audioObjectRawRoute.useParams()

  const audioObjectQuery = useQuery(audioObjectQueryKeys.detail({ audioId: objectId }))
  const { data: audioObject } = audioObjectQuery

  const { mutate } = useAudioObjectUpdateMutation()

  useEffect(() => {
    if (editorInstanceRef.current && audioObjectQuery.isSuccess) {
      editorInstanceRef.current?.getModel()?.setValue(window.JSON.stringify(audioObject, null, 4))
    }
  }, [audioObjectQuery.isSuccess, audioObject])

  if (audioObjectQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!audioObject) {
    return <NotFound />
  }

  if (audioObject.streamId) {
    return <Editor value={audioObject} readOnly />
  }

  return (
    <Editor
      value={audioObject}
      onSubmit={(data) => {
        mutate(
          {
            audioId: objectId,
            audioObjectEdit: data,
          },
          {
            onSuccess: () => {
              enqueueSnackbar(t('domain:Workspace.Audio.audioObjectSaved'), {
                variant: 'success',
              })
            },
          },
        )
      }}
      getEditorInstance={(editorInstance: EditorInstance) => (editorInstanceRef.current = editorInstance)}
    />
  )
}

export const JSON = memo(JSONRaw)
